/* ================================================================
   DTF TURBO — UpCharge-Bridge
   Datei: assets/dtf-upcharge-bridge.js
   Stand: Oktober 2026

   1) Produktseite
      - Baut die Druckpositionen aus der UpCharge-Konfiguration
        (window.mwFeesConfig) auf und verknüpft Expressdruck,
        Profi-Datencheck und Laminierung mit den UpCharge-Fees.
      - Zeigt den Gesamtpreis inkl. Zusatzoptionen sofort an.
      - Prüft Pflichtauswahlen vor dem Hinzufügen zum Warenkorb.
      - Schreibt die Auswahl als Line-Item-Properties in den Warenkorb
        (sichtbar + intern "_dtf_optionen" mit den Fee-IDs).

   2) Warenkorbseite
      - Setzt für jede Zeile mit "_dtf_optionen" die passenden
        UpCharge-Optionen über die App-eigenen Bedienelemente.
        Preisberechnung und Buchung macht ausschließlich UpCharge.
      - Blendet die bereits auf der Produktseite entschiedenen
        UpCharge-Optionen in dieser Zeile aus (keine Doppelauswahl).
      - Sperrt "Zur Kasse", solange Optionen noch übernommen werden.

   3) White-Label-Versand (Warenkorbseite)
      - Checkbox + Empfängerfelder, Speicherung als Warenkorbattribute.

   Es werden keine Preise im Theme hinterlegt. Ohne aktive UpCharge-App
   werden die Optionen auf der Produktseite nicht angeboten.
   ================================================================ */
(function () {
  'use strict';

  if (window.DTFUpchargeBridge) return;
  window.DTFUpchargeBridge = { version: '2026-10' };

  var MARKER_PROP = '_dtf_optionen';
  var APP_FEE_PROP = '_mws_fee';
  var APP_CTA_PROP = '__mws_fee_cta';
  var MAX_ATTEMPTS = 3;
  var ATTEMPT_STORE = 'dtfUpchargeBridgeAttempts';
  var POSITION_TAG = 'addon-druck';

  /* ---------------------------------------------------------------
     Hilfsfunktionen
     --------------------------------------------------------------- */
  function lower(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function asArray(value) {
    if (Array.isArray(value)) return value;
    return value == null ? [] : [value];
  }

  // UpCharge speichert Objekte in Properties teils im Ruby-Hash-Format ("=>").
  function parseLoose(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return null;
    try {
      return JSON.parse(value.replace(/=>/g, ':'));
    } catch (e) {
      return null;
    }
  }

  function waitFor(test, timeout, interval) {
    return new Promise(function (resolve) {
      var start = Date.now();
      (function poll() {
        var result = null;
        try {
          result = test();
        } catch (e) {
          result = null;
        }
        if (result) return resolve(result);
        if (Date.now() - start > timeout) return resolve(null);
        setTimeout(poll, interval || 150);
      })();
    });
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function currencyCode() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  }

  function currencyRate() {
    var rate = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
    return rate > 0 ? rate : 1;
  }

  function formatMoney(cents) {
    try {
      // Geschützte Leerzeichen (U+00A0/U+202F) durch normale ersetzen – saubere Werte in Bestell-Properties.
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: currencyCode() })
        .format(cents / 100)
        .replace(/[\u00A0\u202F]/g, ' ');
    } catch (e) {
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }
  }

  function formatPercent(value) {
    return String(value).replace('.', ',') + ' %';
  }

  function getConfig() {
    var cfg = window.mwFeesConfig;
    return cfg && Array.isArray(cfg.fees) ? cfg : null;
  }

  function getCart() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('cart.js ' + r.status);
      return r.json();
    });
  }

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  /* ---------------------------------------------------------------
     UpCharge-Fees: Auswahl, Anwendbarkeit, Preis
     --------------------------------------------------------------- */
  function isPercentFee(fee) {
    var opt = fee && fee.options && fee.options[0];
    return !!opt && (opt.price_type === 'PERCENT' || !!opt.progressive);
  }

  // UpCharge bucht Prozent-Fees bei MIXED_CTA_AJAX über Cart Transform (Property am Produkt),
  // alle anderen als eigene Gebühren-Zeile.
  function isCtaFee(fee) {
    return fee.api_type === 'MIXED_CTA_AJAX' && isPercentFee(fee);
  }

  function isSelectableProductFee(fee) {
    return (
      fee &&
      fee.type === 'PRODUCT' &&
      fee.input_type === 'CHECKBOX' &&
      !fee.is_checkout &&
      !fee.is_pos &&
      Array.isArray(fee.options) &&
      fee.options.length > 0 &&
      fee.options[0].variant_id
    );
  }

  function feeHasTag(fee, tag) {
    return (fee.conditions || []).some(function (c) {
      return (
        c.type === 'PRODUCT_TAG' &&
        !/NOT/.test(String(c.operator)) &&
        asArray(c.value).map(lower).indexOf(lower(tag)) > -1
      );
    });
  }

  function evalCondition(condition, product) {
    var values = asArray(condition.value).map(lower);
    var subject;
    switch (condition.type) {
      case 'PRODUCT_TAG':
        subject = (product.tags || []).map(lower);
        break;
      case 'PRODUCT_TITLE':
        subject = [lower(product.title)];
        break;
      case 'PRODUCT_TYPE':
        subject = [lower(product.product_type)];
        break;
      case 'PRODUCT_VENDOR':
        subject = [lower(product.vendor)];
        break;
      case 'PRODUCT_COLLECTION':
        subject = (product.collections || []).map(lower);
        break;
      default:
        // Unbekannte Bedingungen entscheidet UpCharge im Warenkorb.
        return true;
    }
    var operator = String(condition.operator || '');
    var hit = values.some(function (v) {
      return subject.some(function (s) {
        return /CONTAIN/.test(operator) ? s.indexOf(v) > -1 : s === v;
      });
    });
    return /NOT/.test(operator) ? !hit : hit;
  }

  function isFeeApplicableToCurrentProduct(fee, cfg) {
    // Bevorzugt die App-eigene Prüfung (gleiche Logik wie im Warenkorb).
    var app = window.mwFees;
    if (app && typeof app.getCurrentProductFeeApplicable === 'function') {
      try {
        var res = app.getCurrentProductFeeApplicable(fee);
        if (res && typeof res.result === 'boolean') return res.result;
      } catch (e) {
        /* Fallback unten */
      }
    }
    var product = cfg.current_product;
    if (!product) return false;
    var results = (fee.conditions || []).map(function (c) {
      return evalCondition(c, product);
    });
    if (!results.length) return true;
    return fee.condition_type === 'ANY' ? results.indexOf(true) > -1 : results.indexOf(false) === -1;
  }

  function feeAmountCents(fee, basePriceCents) {
    var opt = fee.options[0];
    var amount;
    if (opt.price_type === 'PERCENT') {
      amount = (basePriceCents * (Number(opt.price) || 0)) / 100;
      amount = fee.api_type === 'AJAX_API' ? Math.round(amount) : Math.floor(amount + 1e-7);
    } else {
      amount = Math.round((Number(opt.price) || 0) * 100 * currencyRate());
    }
    if (fee.limits && fee.limits.enabled) {
      var min = parseFloat(fee.limits.min);
      var max = parseFloat(fee.limits.max);
      if (!isNaN(min)) amount = Math.max(amount, Math.round(min * 100 * currencyRate()));
      if (!isNaN(max)) amount = Math.min(amount, Math.round(max * 100 * currencyRate()));
    }
    return amount;
  }

  function feeBadgeText(fee) {
    var opt = fee.options[0];
    if (opt.price_type === 'PERCENT') return '+' + formatPercent(opt.price);
    return '+' + formatMoney(feeAmountCents(fee, 0));
  }

  function feeLabel(fee) {
    return String(fee.title || (fee.options[0] && fee.options[0].title) || '').trim();
  }

  /* ===============================================================
     1) PRODUKTSEITE
     =============================================================== */
  function initProductPage(root) {
    if (root.dataset.dtfInit) return;
    root.dataset.dtfInit = '1';

    var formId = root.getAttribute('data-form-id');
    var sectionId = root.getAttribute('data-section-id');
    var form = document.getElementById(formId);
    var container = document.getElementById('MainProduct-' + sectionId) || document;
    var summaryEl = container.querySelector('[data-dtf-summary]');
    var positionsHidden = container.querySelector('[data-dtf-hidden="positions"]');
    var markerHidden = container.querySelector('[data-dtf-hidden="marker"]');
    var group = container.querySelector('[data-dtf-group="' + POSITION_TAG + '"]');
    var singleInputs = Array.prototype.slice.call(container.querySelectorAll('input[data-dtf-addon-tag]'));

    var variantPrices = {};
    try {
      var priceJson = document.getElementById('DtfVariantPrices-' + sectionId);
      variantPrices = priceJson ? JSON.parse(priceJson.textContent) : {};
    } catch (e) {
      variantPrices = {};
    }

    var state = {
      cfg: null,
      positionFees: [], // [{fee, input}]
      singles: [], // [{fee, input}]
    };

    function hideCard(input) {
      var card = input.closest('[data-dtf-addon-card]');
      input.checked = false;
      input.disabled = true;
      if (card) card.hidden = true;
    }

    function findFeeForInput(input, fees) {
      var tag = input.getAttribute('data-dtf-addon-tag');
      var titleHint = lower(input.getAttribute('data-dtf-addon-title'));
      var candidates = fees.filter(function (f) {
        return feeHasTag(f, tag);
      });
      if (titleHint) {
        var byTitle = fees.filter(function (f) {
          return lower(f.title) === titleHint;
        });
        var preferred = candidates.filter(function (f) {
          return lower(f.title) === titleHint;
        });
        candidates = preferred.length ? preferred : candidates.length ? candidates : byTitle;
      }
      return candidates[0] || null;
    }

    function setupSingles(fees) {
      singleInputs.forEach(function (input) {
        var fee = findFeeForInput(input, fees);
        if (!fee) {
          console.warn('DTF Bridge: Keine passende UpCharge-Fee für', input.getAttribute('data-dtf-addon-tag'));
          hideCard(input);
          return;
        }
        var card = input.closest('[data-dtf-addon-card]');
        if (card) card.hidden = false;
        input.dataset.feeId = String(fee.product_id);
        input.value = 'Ja (' + feeBadgeText(fee) + ')';
        var badge = card && card.querySelector('[data-dtf-fee-badge]');
        if (badge) badge.textContent = feeBadgeText(fee);
        input.addEventListener('change', update);
        state.singles.push({ fee: fee, input: input });
      });
    }

    function setupPositions(fees) {
      if (!group) return;
      var list = group.querySelector('[data-dtf-options]');
      var positionFees = fees.filter(function (f) {
        return feeHasTag(f, POSITION_TAG);
      });
      if (!positionFees.length) {
        console.warn('DTF Bridge: Keine Druckpositionen in UpCharge gefunden.');
        group.hidden = true;
        return;
      }
      positionFees.forEach(function (fee, index) {
        var id = 'DtfPos-' + sectionId + '-' + index;
        var label = document.createElement('label');
        label.className = 'dtf-pos';
        label.setAttribute('for', id);

        var input = document.createElement('input');
        input.type = 'checkbox';
        input.id = id;
        input.value = String(fee.product_id);
        input.dataset.feeId = String(fee.product_id);

        var name = document.createElement('span');
        name.className = 'dtf-pos__name';
        name.textContent = feeLabel(fee);

        var price = document.createElement('span');
        price.className = 'dtf-pos__price';
        price.textContent = feeBadgeText(fee);

        label.appendChild(input);
        label.appendChild(name);
        label.appendChild(price);
        list.appendChild(label);

        input.addEventListener('change', function () {
          label.classList.toggle('is-checked', input.checked);
          clearPositionError();
          update();
        });
        state.positionFees.push({ fee: fee, input: input });
      });
      group.hidden = false;
    }

    function selectedPositions() {
      return state.positionFees.filter(function (p) {
        return p.input.checked;
      });
    }

    function selectedSingles() {
      return state.singles.filter(function (s) {
        return s.input.checked && !s.input.disabled;
      });
    }

    function currentVariantId() {
      var idInput = form && form.querySelector('[name="id"]');
      return idInput ? String(idInput.value) : null;
    }

    function currentQuantity() {
      var qty = document.querySelector('input[name="quantity"][form="' + formId + '"]') || (form && form.querySelector('input[name="quantity"]'));
      var value = qty ? parseInt(qty.value, 10) : 1;
      return value > 0 ? value : 1;
    }

    function currentBasePrice() {
      var id = currentVariantId();
      if (id && typeof variantPrices[id] === 'number') return variantPrices[id];
      var selected = state.cfg && state.cfg.current_product && state.cfg.current_product.selected_variant;
      return selected && typeof selected.price === 'number' ? selected.price : 0;
    }

    function syncHiddenInputs() {
      var positions = selectedPositions();
      if (positionsHidden) {
        positionsHidden.value = positions
          .map(function (p) {
            return feeLabel(p.fee);
          })
          .join(', ');
        positionsHidden.disabled = positions.length === 0;
      }

      var offered = state.positionFees.concat(state.singles.filter(function (s) {
        return !s.input.disabled;
      }));
      var selected = positions.concat(selectedSingles());
      if (markerHidden) {
        markerHidden.value = JSON.stringify({
          o: offered.map(function (x) {
            return x.fee.product_id;
          }),
          s: selected.map(function (x) {
            return x.fee.product_id;
          }),
        });
        markerHidden.disabled = offered.length === 0;
      }
    }

    function renderSummary() {
      if (!summaryEl) return;
      var selected = selectedPositions().concat(selectedSingles());
      if (!selected.length) {
        summaryEl.hidden = true;
        summaryEl.innerHTML = '';
        return;
      }
      var base = currentBasePrice();
      var qty = currentQuantity();
      var perUnit = base;
      var rows = [];
      rows.push(['Grundpreis', formatMoney(base)]);
      selected.forEach(function (s) {
        var amount = feeAmountCents(s.fee, base);
        perUnit += amount;
        var opt = s.fee.options[0];
        var label = feeLabel(s.fee) + (opt.price_type === 'PERCENT' ? ' (' + formatPercent(opt.price) + ')' : '');
        rows.push(['+ ' + label, formatMoney(amount)]);
      });

      var html = '<p class="dtf-addon-summary__title">Dein Preis inkl. Zusatzoptionen</p>';
      rows.forEach(function (r) {
        html += '<div class="dtf-addon-summary__row"><span></span><span></span></div>';
      });
      html += '<div class="dtf-addon-summary__row dtf-addon-summary__row--total"><span></span><span></span></div>';
      if (qty > 1) {
        html += '<div class="dtf-addon-summary__row dtf-addon-summary__row--total"><span></span><span></span></div>';
      }
      html += '<p class="dtf-addon-summary__note">Die Zusatzoptionen werden automatisch in den Warenkorb übernommen.</p>';
      summaryEl.innerHTML = html;

      var rowEls = summaryEl.querySelectorAll('.dtf-addon-summary__row');
      rows.forEach(function (r, i) {
        rowEls[i].children[0].textContent = r[0];
        rowEls[i].children[1].textContent = r[1];
      });
      var totalRow = rowEls[rows.length];
      totalRow.children[0].textContent = qty > 1 ? 'Preis pro Stück' : 'Gesamtpreis';
      totalRow.children[1].textContent = formatMoney(perUnit);
      if (qty > 1) {
        var sumRow = rowEls[rows.length + 1];
        sumRow.children[0].textContent = 'Gesamt (' + qty + ' Stück)';
        sumRow.children[1].textContent = formatMoney(perUnit * qty);
      }
      summaryEl.hidden = false;
    }

    function update() {
      syncHiddenInputs();
      renderSummary();
    }
    var scheduleUpdate = debounce(update, 60);

    function showPositionError(message) {
      if (!group) return;
      var err = group.querySelector('.dtf-addon-error');
      group.setAttribute('aria-invalid', 'true');
      if (err) {
        err.textContent = message;
        err.hidden = false;
      }
    }

    function clearPositionError() {
      if (!group) return;
      var err = group.querySelector('.dtf-addon-error');
      group.removeAttribute('aria-invalid');
      if (err) {
        err.textContent = '';
        err.hidden = true;
      }
    }

    function validate() {
      if (group && !group.hidden && group.getAttribute('data-dtf-required') === 'true' && state.positionFees.length) {
        if (!selectedPositions().length) {
          showPositionError('Bitte wähle mindestens eine Druckposition aus.');
          var first = state.positionFees[0].input;
          group.scrollIntoView({ behavior: 'smooth', block: 'center' });
          first.focus({ preventScroll: true });
          return false;
        }
      }
      return true;
    }

    function bindForm() {
      if (!form) return;
      // Capture-Phase: läuft vor dem Standard-Handler von product-form.js
      form.addEventListener(
        'submit',
        function (event) {
          if (!validate()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
          syncHiddenInputs();
        },
        true
      );
    }

    function bindPriceUpdates() {
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
        window.subscribe(window.PUB_SUB_EVENTS.variantChange, function () {
          scheduleUpdate();
        });
      }
      document.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        if (t.name === 'quantity' || t.closest('variant-selects') || t.name === 'id') scheduleUpdate();
      });
      document.addEventListener('input', function (e) {
        if (e.target && e.target.name === 'quantity') scheduleUpdate();
      });
      document.addEventListener('click', function (e) {
        if (e.target && e.target.closest && e.target.closest('quantity-input button')) setTimeout(scheduleUpdate, 0);
      });
      var idInput = form && form.querySelector('[name="id"]');
      if (idInput && window.MutationObserver) {
        new MutationObserver(scheduleUpdate).observe(idInput, { attributes: true, attributeFilter: ['value'] });
      }
    }

    function disableAll() {
      singleInputs.forEach(hideCard);
      if (group) group.hidden = true;
      if (positionsHidden) positionsHidden.disabled = true;
      if (markerHidden) markerHidden.disabled = true;
    }

    waitFor(getConfig, 8000).then(function (cfg) {
      if (!cfg) {
        console.warn('DTF Bridge: UpCharge-Konfiguration nicht gefunden – Zusatzoptionen deaktiviert.');
        disableAll();
        return;
      }
      state.cfg = cfg;
      // App-Instanz kurz abwarten, damit deren Bedingungsprüfung genutzt werden kann.
      waitFor(function () {
        return window.mwFees && typeof window.mwFees.getCurrentProductFeeApplicable === 'function';
      }, 1500).then(function () {
        var fees = cfg.fees.filter(function (f) {
          return isSelectableProductFee(f) && isFeeApplicableToCurrentProduct(f, cfg);
        });
        setupPositions(fees);
        setupSingles(fees);
        bindForm();
        bindPriceUpdates();
        update();
      });
    });

    bindNotificationCheckout();
  }

  /* ---------------------------------------------------------------
     Offene Optionen im Warenkorb ermitteln (Produkt- und Warenkorbseite)
     --------------------------------------------------------------- */
  function lineSelection(item) {
    var marker = parseLoose(item && item.properties && item.properties[MARKER_PROP]);
    if (!marker || !Array.isArray(marker.s)) return null;
    return {
      offered: asArray(marker.o).map(Number),
      selected: marker.s.map(Number),
    };
  }

  function feeForPropertyKey(fee) {
    var title = String(fee.property_title || '');
    var key = title.replace(/\[product_name\]/gi, '').trim();
    var useProductTitle = /\[product_name\]/i.test(title);
    return { key: key, useProductTitle: useProductTitle };
  }

  // UpCharge speichert Produktnamen teils HTML-kodiert ("B&amp;C"). Für den Abgleich normalisieren.
  function decodeEntities(value) {
    var text = String(value == null ? '' : value);
    for (var i = 0; i < 2; i += 1) {
      text = text
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#0*39;|&apos;/gi, "'")
        .replace(/&#(\d+);/g, function (m, n) {
          return String.fromCharCode(parseInt(n, 10));
        })
        .replace(/&amp;/gi, '&');
    }
    return text;
  }

  function normTitle(value) {
    return decodeEntities(value).replace(/[\u00A0\u202F]/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function titleMatches(feeForValue, item) {
    var value = normTitle(feeForValue);
    if (!value) return true;
    var full = normTitle(item.title);
    var product = normTitle(item.product_title);
    var variant = normTitle(item.variant_title);
    if (value === full || value === product) return true;
    return !!product && value.indexOf(product) > -1 && (!variant || variant === 'default title' || value.indexOf(variant) > -1);
  }

  function isCtaApplied(item, fee) {
    var cta = asArray(parseLoose(item.properties && item.properties[APP_CTA_PROP]));
    return cta.some(function (entry) {
      return entry && Number(entry.id) === Number(fee.product_id);
    });
  }

  function collectPending(cart, cfg, isControlChecked) {
    var feesById = {};
    cfg.fees.forEach(function (f) {
      feesById[Number(f.product_id)] = f;
    });

    // Bereits gebuchte Gebührenzeilen; jede Zeile wird höchstens einmal zugeordnet.
    var feeLines = [];
    cart.items.forEach(function (line) {
      var meta = parseLoose(line.properties && line.properties[APP_FEE_PROP]);
      if (meta && meta.id != null) feeLines.push({ id: Number(meta.id), line: line, used: false });
    });

    function takeFeeLine(item, fee, strict) {
      var ref = feeForPropertyKey(fee);
      for (var i = 0; i < feeLines.length; i += 1) {
        var entry = feeLines[i];
        if (entry.used || entry.id !== Number(fee.product_id)) continue;
        var forValue = ref.key && entry.line.properties ? entry.line.properties[ref.key] : null;
        if (!strict || forValue == null || forValue === '' || titleMatches(forValue, item)) {
          entry.used = true;
          return true;
        }
      }
      return false;
    }

    var pending = [];
    var controlled = [];
    var open = [];
    cart.items.forEach(function (item, index) {
      var sel = lineSelection(item);
      if (!sel) return;
      controlled.push({ row: index + 1, item: item, offered: sel.offered });
      sel.selected.forEach(function (id) {
        var fee = feesById[id];
        var task = { row: index + 1, item: item, fee: fee || null, feeId: id };
        if (!fee) {
          pending.push(task);
          return;
        }
        if (isCtaFee(fee)) {
          if (!isCtaApplied(item, fee)) pending.push(task);
          return;
        }
        if (!takeFeeLine(item, fee, true)) open.push(task);
      });
    });

    // Zweite Stufe: Textabgleich schlug fehl. Gilt als übernommen, wenn die App-Checkbox dieser
    // Zeile angehakt ist und es noch eine ungenutzte Gebührenzeile mit derselben Fee-ID gibt.
    open.forEach(function (task) {
      var checked = typeof isControlChecked === 'function' && isControlChecked(task);
      if (!(checked && takeFeeLine(task.item, task.fee, false))) pending.push(task);
    });

    pending.sort(function (a, b) {
      return a.row - b.row;
    });
    return { pending: pending, controlled: controlled, feeLines: feeLines };
  }

  // "Zur Kasse" im Warenkorb-Popup: bei offenen Optionen erst zur Warenkorbseite.
  function bindNotificationCheckout() {
    if (window.DTFUpchargeBridge.notificationBound) return;
    window.DTFUpchargeBridge.notificationBound = true;
    window.addEventListener(
      'click',
      function (event) {
        var btn = event.target && event.target.closest && event.target.closest('#cart-notification-form [name="checkout"]');
        if (!btn || btn.dataset.dtfChecked === '1') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        var cfg = getConfig();
        getCart()
          .then(function (cart) {
            var result = cfg ? collectPending(cart, cfg) : { pending: [], controlled: [] };
            var needsCart = result.pending.length > 0 || (!cfg && cart.items.some(lineSelection));
            if (needsCart) {
              window.location.href = cartUrl();
              return;
            }
            btn.dataset.dtfChecked = '1';
            btn.click();
            setTimeout(function () {
              delete btn.dataset.dtfChecked;
            }, 1500);
          })
          .catch(function () {
            window.location.href = cartUrl();
          });
      },
      true
    );
  }

  /* ===============================================================
     2) WARENKORBSEITE – UpCharge-Optionen automatisch übernehmen
     =============================================================== */
  function readAttempts() {
    try {
      return JSON.parse(sessionStorage.getItem(ATTEMPT_STORE) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function writeAttempts(data) {
    try {
      sessionStorage.setItem(ATTEMPT_STORE, JSON.stringify(data));
    } catch (e) {
      /* privater Modus: ohne Zähler weiter */
    }
  }

  function attemptKey(task) {
    return (task.item.key || task.item.variant_id) + '|' + task.feeId;
  }

  function initCartPage() {
    var itemsRoot = document.getElementById('main-cart-items');
    if (!itemsRoot) return;

    var running = false;
    var rerunTimer = null;
    var failed = {}; // attemptKey -> true
    var lastResult = { pending: [], controlled: [] };

    function rowEl(rowNumber) {
      return document.getElementById('CartItem-' + rowNumber);
    }

    function checkoutButtons() {
      return Array.prototype.slice.call(document.querySelectorAll('#checkout, #CartDrawer-Checkout'));
    }

    function statusEl() {
      var el = document.getElementById('DtfBridgeStatus');
      if (el) return el;
      var btn = checkoutButtons()[0];
      if (!btn || !btn.parentNode) return null;
      el = document.createElement('p');
      el.id = 'DtfBridgeStatus';
      el.className = 'dtf-bridge-status';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.hidden = true;
      btn.parentNode.insertBefore(el, btn);
      return el;
    }

    function logDiagnostics(result, cart) {
      try {
        console.warn(
          'DTF Bridge: offene Optionen',
          result.pending.map(function (t) {
            return {
              zeile: t.row,
              fee_id: t.feeId,
              produkt: t.item.title,
              gebuchte_fee_zeilen: (result.feeLines || [])
                .filter(function (f) {
                  return f.id === Number(t.feeId);
                })
                .map(function (f) {
                  return f.line.properties;
                })
            };
          }),
          'Warenkorb-Zeilen:',
          cart.items.length
        );
      } catch (e) {
        /* nur Diagnose */
      }
    }

    function setDynamicButtons(hide) {
      Array.prototype.forEach.call(document.querySelectorAll('.cart__dynamic-checkout-buttons'), function (el) {
        if (hide) {
          if (el.dataset.dtfHidden !== '1') {
            el.dataset.dtfHidden = '1';
            el.dataset.dtfDisplay = el.style.display || '';
            el.style.display = 'none';
          }
        } else if (el.dataset.dtfHidden === '1') {
          el.style.display = el.dataset.dtfDisplay || '';
          delete el.dataset.dtfHidden;
          delete el.dataset.dtfDisplay;
        }
      });
    }

    function setPending(isPending, message, isError) {
      setDynamicButtons(isPending);
      checkoutButtons().forEach(function (btn) {
        if (isPending) {
          if (!btn.disabled) {
            btn.disabled = true;
            btn.dataset.dtfDisabled = '1';
          }
        } else if (btn.dataset.dtfDisabled === '1') {
          btn.disabled = false;
          delete btn.dataset.dtfDisabled;
        }
      });
      var el = statusEl();
      if (!el) return;
      el.hidden = !isPending;
      el.textContent = isPending ? message : '';
      el.classList.toggle('dtf-bridge-status--error', !!isError);
    }

    function hideDecidedControls(result) {
      result.controlled.forEach(function (c) {
        var row = rowEl(c.row);
        if (!row) return;
        c.offered.forEach(function (feeId) {
          var key = (c.item.key || c.item.variant_id) + '|' + feeId;
          var wrappers = row.querySelectorAll('[data-fee-id="' + feeId + '"]');
          Array.prototype.forEach.call(wrappers, function (w) {
            w.classList.toggle('dtf-mw-decided', !failed[key]);
          });
        });
      });
    }

    function findControl(task) {
      var row = rowEl(task.row);
      if (!row) return null;
      return row.querySelector('[data-fee-id="' + task.feeId + '"] input[type="checkbox"]');
    }

    function markFailed(task) {
      failed[attemptKey(task)] = true;
    }

    function run() {
      if (running) return;
      running = true;
      clearTimeout(rerunTimer);

      Promise.all([waitFor(getConfig, 8000), getCart()])
        .then(function (res) {
          var cfg = res[0];
          var cart = res[1];
          if (!cfg) {
            var anyMarked = cart.items.some(lineSelection);
            if (anyMarked) setPending(true, 'Zusatzoptionen konnten nicht geladen werden. Bitte lade die Seite neu oder kontaktiere uns.', true);
            return null;
          }
          var result = collectPending(cart, cfg, function (t) {
            var c = t.fee && findControl(t);
            return !!(c && c.checked);
          });
          lastResult = result;
          hideDecidedControls(result);

          if (!result.pending.length) {
            writeAttempts({});
            setPending(false);
            return null;
          }

          var attempts = readAttempts();
          var unknown = result.pending.filter(function (t) {
            return !t.fee;
          });
          unknown.forEach(markFailed);

          // Prozent-Fees (Cart Transform) zuerst, danach feste Gebühren-Zeilen.
          var actionable = result.pending.filter(function (t) {
            return t.fee && (attempts[attemptKey(t)] || 0) < MAX_ATTEMPTS;
          });
          var cta = actionable.filter(function (t) {
            return isCtaFee(t.fee);
          });
          var batch = cta.length ? cta : actionable;

          if (!batch.length) {
            result.pending.forEach(markFailed);
            hideDecidedControls(result);
            logDiagnostics(result, cart);
            setPending(
              true,
              'Einige Zusatzoptionen konnten nicht automatisch übernommen werden. Bitte wähle sie direkt beim Produkt im Warenkorb aus oder kontaktiere uns.',
              true
            );
            return null;
          }

          setPending(true, 'Deine Zusatzoptionen werden übernommen …', false);

          return waitFor(function () {
            return batch.every(function (t) {
              return findControl(t);
            }) || null;
          }, 8000).then(function () {
            var clicked = 0;
            batch.forEach(function (task) {
              var input = findControl(task);
              var key = attemptKey(task);
              if (!input) {
                markFailed(task);
                return;
              }
              attempts[key] = (attempts[key] || 0) + 1;
              if (!input.checked) {
                input.checked = true;
                input.dispatchEvent(new Event('change', { bubbles: true }));
                clicked += 1;
              }
            });
            writeAttempts(attempts);
            hideDecidedControls(result);
            if (!clicked) {
              setPending(
                true,
                'Einige Zusatzoptionen konnten nicht automatisch übernommen werden. Bitte wähle sie direkt beim Produkt im Warenkorb aus oder kontaktiere uns.',
                true
              );
            }
            // UpCharge lädt die Warenkorbseite nach dem Buchen in der Regel neu.
            // Falls nicht, erneut prüfen.
            return clicked ? 'retry' : null;
          });
        })
        .catch(function (e) {
          console.warn('DTF Bridge: Warenkorb konnte nicht geprüft werden', e);
        })
        .then(function (next) {
          running = false;
          if (next === 'retry') {
            rerunTimer = setTimeout(function () {
              waitFor(function () {
                return !(window.mwFees && window.mwFees.cartExecInProgress);
              }, 10000).then(run);
            }, 2500);
          }
        });
    }

    // Zusätzliche Absicherung: Checkout nicht auslösen, solange Optionen offen sind.
    window.addEventListener(
      'click',
      function (event) {
        var btn = event.target && event.target.closest && event.target.closest('#checkout, #CartDrawer-Checkout');
        if (!btn) return;
        if (lastResult.pending.length) {
          event.preventDefault();
          event.stopImmediatePropagation();
          run();
        }
      },
      true
    );

    // App-Widgets und Dawn rendern Warenkorbzeilen neu → Ausblendung erneut anwenden.
    var reapply = debounce(function () {
      hideDecidedControls(lastResult);
    }, 100);
    if (window.MutationObserver) {
      new MutationObserver(reapply).observe(document.getElementById('MainContent') || document.body, {
        childList: true,
        subtree: true,
      });
    }
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, debounce(run, 300));
    }

    run();
  }

  /* ===============================================================
     3) WHITE-LABEL-VERSAND (Warenkorbseite)
     =============================================================== */
  function initWhiteLabel(root) {
    if (root.dataset.dtfInit) return;
    root.dataset.dtfInit = '1';

    var toggle = root.querySelector('[data-wl-toggle]');
    var fieldset = root.querySelector('[data-wl-fields]');
    var flag = root.querySelector('[data-wl-flag]');
    var clearInputs = Array.prototype.slice.call(root.querySelectorAll('[data-wl-clear]'));
    var inputs = Array.prototype.slice.call(root.querySelectorAll('[data-wl-field]'));
    var summaryError = root.querySelector('[data-wl-summary-error]');
    var saveChain = Promise.resolve();
    var dirty = false;

    var PATTERNS = {
      zip: /^[A-Za-z0-9][A-Za-z0-9 \-]{2,9}$/,
    };

    function fieldError(input) {
      var value = input.value.trim();
      if (!value) return input.getAttribute('data-wl-required-message') || 'Bitte ausfüllen.';
      var type = input.getAttribute('data-wl-field');
      if (PATTERNS[type] && !PATTERNS[type].test(value)) return input.getAttribute('data-wl-pattern-message') || 'Bitte prüfe deine Eingabe.';
      return '';
    }

    function showFieldError(input, message) {
      var err = document.getElementById(input.id + '-error');
      input.setAttribute('aria-invalid', message ? 'true' : 'false');
      if (err) {
        err.querySelector('[data-wl-error-text]').textContent = message;
        err.hidden = !message;
      }
    }

    function validateAll(focusFirst) {
      if (!toggle.checked) return true;
      var firstInvalid = null;
      inputs.forEach(function (input) {
        var message = fieldError(input);
        showFieldError(input, message);
        if (message && !firstInvalid) firstInvalid = input;
      });
      if (summaryError) {
        summaryError.hidden = !firstInvalid;
        summaryError.textContent = firstInvalid ? 'Bitte vervollständige die Empfängeradresse für den White-Label-Versand.' : '';
      }
      if (firstInvalid && focusFirst) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalid.focus({ preventScroll: true });
      }
      return !firstInvalid;
    }

    function attributesPayload() {
      var attrs = {};
      attrs[flag.getAttribute('data-wl-attr')] = toggle.checked ? 'Ja' : '';
      inputs.forEach(function (input) {
        attrs[input.getAttribute('data-wl-attr')] = toggle.checked ? input.value.trim() : '';
      });
      return attrs;
    }

    function save() {
      dirty = false;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var body = JSON.stringify({ attributes: attributesPayload() });
      saveChain = saveChain
        .catch(function () {})
        .then(function () {
          return fetch(root + 'cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: body,
          });
        })
        .then(function (r) {
          if (!r.ok) throw new Error('cart/update ' + r.status);
        })
        .catch(function (e) {
          dirty = true;
          console.warn('DTF White-Label: Speichern fehlgeschlagen', e);
        });
      return saveChain;
    }
    var scheduleSave = debounce(save, 500);

    function applyToggleState(focus) {
      var on = toggle.checked;
      fieldset.hidden = !on;
      fieldset.disabled = !on;
      toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
      flag.value = on ? 'Ja' : '';
      clearInputs.forEach(function (c) {
        c.disabled = on;
      });
      if (!on) {
        inputs.forEach(function (input) {
          showFieldError(input, '');
        });
        if (summaryError) summaryError.hidden = true;
      } else if (focus && inputs[0]) {
        inputs[0].focus();
      }
    }

    toggle.addEventListener('change', function () {
      applyToggleState(true);
      dirty = true;
      save();
    });

    inputs.forEach(function (input) {
      input.addEventListener('input', function () {
        dirty = true;
        if (input.getAttribute('aria-invalid') === 'true' && !fieldError(input)) showFieldError(input, '');
        scheduleSave();
      });
      input.addEventListener('blur', function () {
        if (toggle.checked && input.value.trim() !== '') showFieldError(input, fieldError(input));
        if (dirty) save();
      });
    });

    // Checkout-Absicherung (Capture auf window, läuft vor App- und Theme-Handlern).
    function guard(event) {
      if (!validateAll(true)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (dirty) {
        event.preventDefault();
        event.stopImmediatePropagation();
        var btn = event.target && event.target.closest ? event.target.closest('[name="checkout"], #checkout') : null;
        save().then(function () {
          if (btn) btn.click();
        });
      }
    }

    window.addEventListener(
      'click',
      function (event) {
        var btn = event.target && event.target.closest && event.target.closest('#checkout, #CartDrawer-Checkout, .cart__checkout-button');
        if (btn) guard(event);
      },
      true
    );
    var cartForm = document.getElementById('cart');
    if (cartForm) {
      cartForm.addEventListener(
        'submit',
        function (event) {
          if (!validateAll(true)) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        },
        true
      );
    }

    applyToggleState(false);
    hideAppWhiteLabelFee();
  }

  // Das bisherige UpCharge-Textfeld „White-Label-Versand“ ausblenden, damit es keine Doppelabfrage gibt.
  function hideAppWhiteLabelFee() {
    waitFor(getConfig, 8000).then(function (cfg) {
      if (!cfg) return;
      var ids = cfg.fees
        .filter(function (f) {
          return f.type === 'CART' && /white[\s-]*label/i.test(String(f.title));
        })
        .map(function (f) {
          return String(f.product_id);
        });
      if (!ids.length) return;
      var style = document.createElement('style');
      style.textContent = ids
        .map(function (id) {
          return '[data-fee-id="' + id + '"]';
        })
        .join(',') + '{display:none !important;}';
      document.head.appendChild(style);
    });
  }

  /* ---------------------------------------------------------------
     Start
     --------------------------------------------------------------- */
  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-dtf-addons-root]'), initProductPage);
    var wl = document.querySelector('[data-dtf-white-label]');
    if (wl) initWhiteLabel(wl);
    if (document.getElementById('main-cart-items')) initCartPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
